import { useMemo, useState } from 'react';

import { motion } from 'framer-motion';

import { DraftType } from '../../../../../../../../../../../backend/src/types/types';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Case } from '@/api/types/backend-dto-types';
import {
  KimQuestionOnboardingModal,
  KimExtractionOnboardingModal,
  KimDraftOnboardingModal,
} from '@/components/molecules/Modals/Onboarding/KimOnboardingModals';
import { ExecuteKimSearch, KimMode } from '@/types';

enum KimChecklistStep {
  AskKimQuestion = 'ask-kim-question',
  UseKimExtraction = 'use-kim-extraction',
  UseKimDraft = 'use-kim-draft',
}

interface OnboardingItem {
  id: KimChecklistStep;
  title: string;
  description: string;
  icon: React.ReactNode;
  mode: KimMode;
  draftType?: DraftType | null;
  action: () => void;
}

interface KimOnboardingProps {
  executeSearch: ExecuteKimSearch;
  setMode: (mode: KimMode) => void;
  setDraftType: (draftType: DraftType | null) => void;
  setQuestionValue: (value: string) => void;
  caseData?: Case;
}

const OnboardingItemComponent = ({ item }: { item: OnboardingItem }) => {
  return (
    <motion.div
      key={item.id}
      onClick={() => item.action()}
      className="group relative cursor-pointer overflow-hidden rounded-lg border border-l-4 border-purple-200 bg-white p-4 pr-16 shadow-sm transition-all duration-300 hover:border-purple-300 hover:shadow-lg"
      data-testid={`kim-onboarding-item-${item.id}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      whileHover={{
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        transition: { duration: 0.1 },
      }}
      transition={{
        duration: 0.2,
        type: 'easeInOut',
      }}
    >
      <div className="group-hover:bg-opacity-80 absolute top-0 right-0 flex h-full w-12 items-center justify-center bg-purple-100">
        <span className="iconify lucide--arrow-right h-4 w-4 text-purple-600 transition-transform duration-200" />
      </div>

      <div className="flex items-start">
        <div className="relative mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 transition-shadow duration-200 group-hover:shadow-sm">
          <span>{item.icon}</span>
        </div>

        <div className="flex-grow">
          <h3 className="text-base font-medium">{item.title}</h3>
          <p className="mt-1 text-xs text-gray-600">{item.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

export const KimOnboarding = ({
  executeSearch,
  setMode,
  setDraftType,
  setQuestionValue,
  caseData,
}: KimOnboardingProps) => {
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [extractionModalOpen, setExtractionModalOpen] = useState(false);
  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const onboardingItems: OnboardingItem[] = useMemo(() => {
    return [
      {
        id: KimChecklistStep.AskKimQuestion,
        title: 'Ask a question',
        description: 'Try asking Kim a question about your documents',
        icon: <span className="iconify lucide--circle-help h-5 w-5 text-purple-600" />,
        mode: KimMode.QuestionFlow,
        action: () => {
          trackCustomEvent(TrackingEvents.KimOnboardingClickQuestionFlow);
          setQuestionModalOpen(true);
        },
      },
      {
        id: KimChecklistStep.UseKimExtraction,
        title: 'Extract information',
        description: 'Use Kim to extract information into a table',
        icon: <span className="iconify lucide--sheet h-5 w-5 text-purple-600" />,
        mode: KimMode.ExtractionFlow,
        action: () => {
          trackCustomEvent(TrackingEvents.KimOnboardingClickExtractionFlow);
          setExtractionModalOpen(true);
        },
      },
      {
        id: KimChecklistStep.UseKimDraft,
        title: 'Draft documents',
        description: 'Use Kim to draft documents based on your case facts',
        icon: <span className="iconify lucide--file-text h-5 w-5 text-purple-600" />,
        mode: KimMode.DraftFlow,
        draftType: DraftType.Claim,
        action: () => {
          trackCustomEvent(TrackingEvents.KimOnboardingClickDraftFlow);
          setDraftModalOpen(true);
        },
      },
    ];
  }, []);

  return (
    <div className="flex w-full flex-col items-center justify-center p-8">
      <div className="w-full max-w-2xl">
        <div className="mb-6 text-center">
          <h2 className="mb-2 text-2xl font-bold text-gray-800">Need some help with Kim?</h2>
          <p className="mb-1 text-gray-600">
            Kim is Wexler's AI agent for gathering information, and running tasks on your case.
          </p>
          <p className="text-sm text-gray-600">
            Click through the items below to get familiar with Kim's different workflows.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {onboardingItems.map((item) => (
            <OnboardingItemComponent key={item.id} item={item} />
          ))}
        </div>
      </div>

      {/* Kim Onboarding Modals */}
      <KimQuestionOnboardingModal
        isOpen={questionModalOpen}
        setIsOpen={setQuestionModalOpen}
        caseData={caseData}
        executeSearch={executeSearch}
        setMode={setMode}
        setDraftType={setDraftType}
        setQuestionValue={setQuestionValue}
      />

      <KimExtractionOnboardingModal
        isOpen={extractionModalOpen}
        setIsOpen={setExtractionModalOpen}
        caseData={caseData}
        executeSearch={executeSearch}
        setMode={setMode}
        setDraftType={setDraftType}
        setQuestionValue={setQuestionValue}
      />

      <KimDraftOnboardingModal
        isOpen={draftModalOpen}
        setIsOpen={setDraftModalOpen}
        caseData={caseData}
        executeSearch={executeSearch}
        setMode={setMode}
        setDraftType={setDraftType}
        setQuestionValue={setQuestionValue}
      />
    </div>
  );
};
